import { useEffect, useMemo, useRef, useState } from "react";
import type { Room } from "@colyseus/sdk";
import {
  getAction,
  getActionsForSection,
  getSectionDefinition,
  type ActionId,
  type CrewState,
  type NoticeMessage,
  type SectionId
} from "@wearecrew/shared";
import { ShipMap } from "./components/ShipMap";
import { CrewNetwork, type CrewRoomConnection } from "./network";
import { snapshotCrewState, type CrewSnapshot, type PlayerView, type SectionView } from "./types";

const RECONNECT_KEY = "wearecrew.reconnection-token";
const NAME_KEY = "wearecrew.player-name";

function formatTimer(seconds: number): string {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
  const remainder = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${remainder}`;
}

function formatMetric(value: number): string {
  return Math.round(value).toString();
}

function Landing({
  busy,
  error,
  initialCode,
  onCreate,
  onQuickPlay,
  onJoin
}: {
  busy: boolean;
  error: string;
  initialCode: string;
  onCreate: (name: string) => void;
  onQuickPlay: (name: string) => void;
  onJoin: (name: string, code: string) => void;
}) {
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? "");
  const [code, setCode] = useState(initialCode);

  const submitName = (): string | undefined => {
    const cleaned = name.trim().slice(0, 18);
    if (!cleaned) return undefined;
    localStorage.setItem(NAME_KEY, cleaned);
    return cleaned;
  };

  return (
    <main className="landing-shell">
      <div className="stars stars-one" />
      <div className="stars stars-two" />
      <section className="landing-card">
        <div className="eyebrow">COOPERATIVE SHIP SYSTEMS</div>
        <h1>WE ARE <span>CREW</span></h1>
        <p className="landing-lead">
          Один корабль. Один экипаж. Любое решение меняет ситуацию для всех.
        </p>

        <label className="field-label" htmlFor="player-name">Позывной</label>
        <input
          id="player-name"
          className="text-field"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={18}
          placeholder="Например, Вектор"
          disabled={busy}
          autoFocus
        />

        <div className="primary-actions">
          <button className="button button-primary" disabled={busy || !name.trim()} onClick={() => {
            const validName = submitName();
            if (validName) onCreate(validName);
          }}>
            Создать экипаж
          </button>
          <button className="button button-secondary" disabled={busy || !name.trim()} onClick={() => {
            const validName = submitName();
            if (validName) onQuickPlay(validName);
          }}>
            Быстрая игра
          </button>
        </div>

        <div className="join-divider"><span>или войти по коду</span></div>
        <div className="join-row">
          <input
            className="text-field code-field"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 16))}
            placeholder="Код комнаты"
            disabled={busy}
          />
          <button className="button button-ghost" disabled={busy || !name.trim() || !code.trim()} onClick={() => {
            const validName = submitName();
            if (validName) onJoin(validName, code.trim());
          }}>
            Войти
          </button>
        </div>

        {busy && <div className="status-message"><span className="spinner" /> Подключение к кораблю…</div>}
        {error && <div className="error-message">{error}</div>}
        <p className="prototype-note">Ранний сетевой прототип · 1–4 игрока · клавиатура не требуется</p>
      </section>
    </main>
  );
}

function Metric({ label, value, suffix, danger }: { label: string; value: number; suffix?: string; danger?: boolean }) {
  return (
    <div className={`metric ${danger ? "metric-danger" : ""}`}>
      <span>{label}</span>
      <strong>{formatMetric(value)}{suffix}</strong>
    </div>
  );
}

function SectionPanel({
  snapshot,
  player,
  section,
  now,
  onAction,
  onCancelAction,
  onToggleSuit
}: {
  snapshot: CrewSnapshot;
  player: PlayerView;
  section: SectionView;
  now: number;
  onAction: (actionId: ActionId) => void;
  onCancelAction: () => void;
  onToggleSuit: () => void;
}) {
  const definition = getSectionDefinition(section.id);
  const actions = getActionsForSection(section.id);
  const activeAction = getAction(player.activeActionId);
  const actionProgress = activeAction
    ? Math.min(1, Math.max(0, (now - player.actionStartedAt) / Math.max(1, player.actionEndsAt - player.actionStartedAt)))
    : 0;
  const travelling = player.travelEndsAt > now;

  return (
    <aside className="control-panel">
      <div className="panel-kicker">ТЕКУЩАЯ СЕКЦИЯ</div>
      <h2>{definition?.name ?? section.id}</h2>
      <p className="section-description">{definition?.description}</p>

      <div className="section-metrics">
        <Metric label="Кислород" value={section.oxygen} suffix="%" danger={section.oxygen < 50} />
        <Metric label="Давление" value={section.pressure} suffix=" kPa" danger={section.pressure < 50} />
        <Metric label="Температура" value={section.temperature} suffix="°" danger={section.temperature > 60} />
        <Metric label="Дым" value={section.smoke} suffix="%" danger={section.smoke > 20} />
      </div>

      <div className="personal-status">
        <div>
          <span>Здоровье</span>
          <strong>{formatMetric(player.health)}%</strong>
        </div>
        <button
          className={`suit-button ${player.suitOn ? "suit-on" : ""}`}
          disabled={travelling || Boolean(activeAction)}
          onClick={onToggleSuit}
        >
          {player.suitOn ? "Скафандр надет" : "Надеть скафандр"}
        </button>
      </div>

      {travelling && (
        <div className="active-task travel-task">
          <div className="task-title">Переход: {getSectionDefinition(player.travelToSectionId)?.shortName}</div>
          <div className="progress-track"><span style={{ width: `${Math.min(100, Math.max(0, (now - player.travelStartedAt) / Math.max(1, player.travelEndsAt - player.travelStartedAt) * 100))}%` }} /></div>
          <div className="task-time">{Math.max(0, (player.travelEndsAt - now) / 1000).toFixed(1)} сек.</div>
        </div>
      )}

      {activeAction && (
        <div className="active-task">
          <div className="task-title">{activeAction.name}</div>
          <div className="progress-track"><span style={{ width: `${actionProgress * 100}%` }} /></div>
          <div className="task-footer">
            <span>{Math.max(0, (player.actionEndsAt - now) / 1000).toFixed(1)} сек.</span>
            <button onClick={onCancelAction}>Отменить</button>
          </div>
        </div>
      )}

      {snapshot.phase === "playing" && !activeAction && !travelling && (
        <div className="section-actions">
          <div className="panel-kicker">ДОСТУПНЫЕ ДЕЙСТВИЯ</div>
          {actions.map((action) => (
            <button
              key={action.id}
              className={`action-button action-${action.tone}`}
              onClick={() => onAction(action.id)}
            >
              <span>{action.name}</span>
              <small>{action.description}</small>
              <em>{action.durationSeconds} сек.</em>
            </button>
          ))}
        </div>
      )}

      {snapshot.phase === "lobby" && (
        <div className="lobby-tip">До начала миссии можно осмотреть корабль и проверить время переходов.</div>
      )}
    </aside>
  );
}

function LobbyControls({
  snapshot,
  localPlayer,
  onReady,
  onStart
}: {
  snapshot: CrewSnapshot;
  localPlayer: PlayerView;
  onReady: (ready: boolean) => void;
  onStart: () => void;
}) {
  const isOwner = snapshot.ownerId === localPlayer.id;
  const allReady = snapshot.players.length > 0 && snapshot.players.every((player) => player.ready);
  const inviteUrl = `${window.location.origin}/?room=${snapshot.roomCode}`;

  return (
    <section className="lobby-banner">
      <div>
        <div className="panel-kicker">СБОР ЭКИПАЖА · {snapshot.players.length}/4</div>
        <strong>{snapshot.visibility === "private" ? "Частная команда" : "Открытый экипаж"}</strong>
      </div>
      <div className="room-code">
        <span>КОД</span>
        <b>{snapshot.roomCode}</b>
        <button onClick={() => void navigator.clipboard.writeText(inviteUrl)}>Копировать ссылку</button>
      </div>
      <div className="lobby-buttons">
        <button className={`button ${localPlayer.ready ? "button-ready" : "button-secondary"}`} onClick={() => onReady(!localPlayer.ready)}>
          {localPlayer.ready ? "Готов ✓" : "Я готов"}
        </button>
        {isOwner && (
          <button className="button button-primary" disabled={!allReady} onClick={onStart}>
            Запустить миссию
          </button>
        )}
      </div>
    </section>
  );
}

function CrewBar({ players, ownerId }: { players: PlayerView[]; ownerId: string }) {
  return (
    <div className="crew-bar">
      {players.map((player) => (
        <div className={`crew-member ${!player.connected ? "crew-offline" : ""}`} key={player.id}>
          <i style={{ background: player.color }} />
          <div><strong>{player.name}</strong><span>{player.id === ownerId ? "капитан" : player.ready ? "готов" : "экипаж"}</span></div>
          <b>{Math.round(player.health)}%</b>
        </div>
      ))}
      {Array.from({ length: Math.max(0, 4 - players.length) }, (_, index) => (
        <div className="crew-member crew-empty" key={`empty-${index}`}>Свободное место</div>
      ))}
    </div>
  );
}

function GameShell({
  room,
  snapshot,
  connectionState,
  notice,
  onLeave
}: {
  room: CrewRoomConnection;
  snapshot: CrewSnapshot;
  connectionState: string;
  notice: NoticeMessage | null;
  onLeave: () => void;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, []);

  const localPlayer = snapshot.players.find((player) => player.id === room.sessionId);
  const localSection = snapshot.sections.find((section) => section.id === localPlayer?.sectionId);
  const averageOxygen = snapshot.sections.reduce((sum, section) => sum + section.oxygen, 0) / Math.max(1, snapshot.sections.length);

  if (!localPlayer || !localSection) {
    return <div className="loading-screen"><span className="spinner" /> Синхронизация корабля…</div>;
  }

  const send = (type: string, payload?: unknown) => room.send(type, payload);

  return (
    <main className={`game-shell alert-${snapshot.alertLevel}`}>
      <header className="topbar">
        <div className="brand-small"><span>WE ARE</span> CREW</div>
        <div className="mission-clock"><span>ВРЕМЯ МИССИИ</span><strong>{formatTimer(snapshot.missionElapsed)}</strong></div>
        <div className="ship-vitals">
          <Metric label="O₂ корабля" value={averageOxygen} suffix="%" danger={averageOxygen < 55} />
          <Metric label="Реактор" value={snapshot.reactorStability} suffix="%" danger={snapshot.reactorStability < 40} />
          <Metric label="Корпус" value={snapshot.hullIntegrity} suffix="%" danger={snapshot.hullIntegrity < 40} />
          <Metric label="Энергосеть" value={snapshot.gridIntegrity} suffix="%" danger={snapshot.gridIntegrity < 50} />
        </div>
        <button className="leave-button" onClick={onLeave}>Покинуть</button>
      </header>

      {snapshot.phase === "lobby" && (
        <LobbyControls
          snapshot={snapshot}
          localPlayer={localPlayer}
          onReady={(ready) => send("ready", { ready })}
          onStart={() => send("start")}
        />
      )}

      {snapshot.phase === "results" && (
        <section className={`results-banner result-${snapshot.result}`}>
          <div><span>{snapshot.result === "success" ? "МИССИЯ ВЫПОЛНЕНА" : "КОРАБЛЬ ПОТЕРЯН"}</span><strong>{snapshot.result === "success" ? "Экипаж стабилизировал все системы" : "Критические системы больше не отвечают"}</strong></div>
          {snapshot.ownerId === localPlayer.id && <button className="button button-primary" onClick={() => send("restart")}>Новая попытка</button>}
        </section>
      )}

      <div className="game-grid">
        <section className="map-column">
          <ShipMap
            players={snapshot.players}
            sections={snapshot.sections}
            localPlayerId={localPlayer.id}
            now={now}
            onMove={(sectionId) => send("move", { targetSectionId: sectionId as SectionId })}
          />
          <CrewBar players={snapshot.players} ownerId={snapshot.ownerId} />
        </section>

        <SectionPanel
          snapshot={snapshot}
          player={localPlayer}
          section={localSection}
          now={now}
          onAction={(actionId) => send("action", { actionId })}
          onCancelAction={() => send("cancel-action")}
          onToggleSuit={() => send("toggle-suit")}
        />
      </div>

      <section className="event-log">
        <div className="panel-kicker">ЖУРНАЛ СОБЫТИЙ</div>
        <div className="log-list">
          {snapshot.logs.map((entry) => (
            <div className={`log-entry log-${entry.level}`} key={entry.id}>
              <time>{new Date(entry.at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</time>
              <span>{entry.text}</span>
            </div>
          ))}
        </div>
      </section>

      {connectionState !== "connected" && <div className="connection-overlay"><span className="spinner" /> Восстанавливаем связь с кораблём…</div>}
      {notice && <div className={`toast toast-${notice.level}`}>{notice.text}</div>}
    </main>
  );
}

export default function App() {
  const network = useMemo(() => new CrewNetwork(), []);
  const roomRef = useRef<Room<CrewState> | null>(null);
  const [room, setRoom] = useState<CrewRoomConnection | null>(null);
  const [snapshot, setSnapshot] = useState<CrewSnapshot | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [connectionState, setConnectionState] = useState("connected");
  const [notice, setNotice] = useState<NoticeMessage | null>(null);
  const noticeTimer = useRef<number | undefined>(undefined);

  const attachRoom = (nextRoom: CrewRoomConnection) => {
    roomRef.current = nextRoom;
    setRoom(nextRoom);
    sessionStorage.setItem(RECONNECT_KEY, nextRoom.reconnectionToken);

    const publish = (state: CrewState) => setSnapshot(snapshotCrewState(state));
    nextRoom.onStateChange(publish);
    publish(nextRoom.state);
    nextRoom.onMessage<NoticeMessage>("notice", (message) => {
      setNotice(message);
      if (noticeTimer.current !== undefined) window.clearTimeout(noticeTimer.current);
      noticeTimer.current = window.setTimeout(() => setNotice(null), 3500);
    });
    nextRoom.onDrop(() => setConnectionState("reconnecting"));
    nextRoom.onReconnect(() => {
      setConnectionState("connected");
      sessionStorage.setItem(RECONNECT_KEY, nextRoom.reconnectionToken);
    });
    nextRoom.onLeave(() => {
      sessionStorage.removeItem(RECONNECT_KEY);
      roomRef.current = null;
      setRoom(null);
      setSnapshot(null);
      setConnectionState("connected");
    });
  };

  useEffect(() => {
    const reconnectToken = sessionStorage.getItem(RECONNECT_KEY);
    if (!reconnectToken) {
      setBusy(false);
      return;
    }

    void network.reconnect(reconnectToken)
      .then(attachRoom)
      .catch(() => sessionStorage.removeItem(RECONNECT_KEY))
      .finally(() => setBusy(false));
  }, [network]);

  useEffect(() => () => {
    if (noticeTimer.current !== undefined) window.clearTimeout(noticeTimer.current);
  }, []);

  const connect = async (operation: () => Promise<CrewRoomConnection>) => {
    setBusy(true);
    setError("");
    try {
      attachRoom(await operation());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось подключиться к комнате.");
    } finally {
      setBusy(false);
    }
  };

  const leave = () => {
    sessionStorage.removeItem(RECONNECT_KEY);
    void roomRef.current?.leave();
  };

  const initialCode = new URLSearchParams(window.location.search).get("room") ?? "";
  if (!room || !snapshot) {
    return (
      <Landing
        busy={busy}
        error={error}
        initialCode={initialCode}
        onCreate={(name) => void connect(() => network.createPrivate(name))}
        onQuickPlay={(name) => void connect(() => network.quickPlay(name))}
        onJoin={(name, code) => void connect(() => network.joinByCode(code, name))}
      />
    );
  }

  return (
    <GameShell
      room={room}
      snapshot={snapshot}
      connectionState={connectionState}
      notice={notice}
      onLeave={leave}
    />
  );
}

